import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { get } from "../../utils/api";

interface ModuleFieldsMultiSelectProps {
  mapped_collection: {
    collection: string;
    primary_id_field: string;
    primary_name_field: string;
  };
  values?: Array<{ [key: string]: any }>; // Assuming values contain only the necessary value objects without labels.
  onChange: (options: Array<{ [key: string]: any }>) => void;
}

function ModuleFieldsMultiSelect({
  mapped_collection,
  values = [],
  onChange,
}: ModuleFieldsMultiSelectProps) {
  const { collection, primary_id_field, primary_name_field } = mapped_collection;
  const [options, setOptions] = useState<Array<{ value: any; label: string }>>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<{ value: any; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFields = useCallback(() => {
    setLoading(true);
    const params = {
      page: 1,
      limit: 1000,
      filter: {},
      sortBy: {},
    };

    get(`${collection}`, params)
      .then((response) => {
        if (response?.data?.data) {
          const fieldOptions = response.data.data.map((field: any) => ({
            value: field[primary_id_field],
            label: field[primary_name_field],
          }));
          setOptions(fieldOptions);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch fields:", error);
        setError("Failed to load options");
        setLoading(false);
      });
  }, [collection, primary_id_field, primary_name_field]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  useEffect(() => {
    if (options.length > 0) {
      const initialValues = values.map((field: any) => options.find(option => option.value === field[primary_id_field]) || {
        value: field[primary_id_field],
        label: field[primary_name_field]
      });
      setSelectedOptions(initialValues);
    }
  }, [values, options, primary_id_field, primary_name_field]);

  const handleChange = (selected: any) => {
    setSelectedOptions(selected);
    onChange(selected.map((option: any) => ({
      [primary_id_field]: option.value,
      [primary_name_field]: option.label,
    })));
  };

  const handleRetry = () => {
    setError(null);
    fetchFields();
  };

  if (loading) return <p>Loading...</p>;
  if (error) return (
    <div>
      <p>{error}</p>
      <button onClick={handleRetry} style={{ padding: "8px 16px", cursor: "pointer" }}>Retry</button>
    </div>
  );

  return (
    <Select
      isMulti
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      className="basic-multi-select"
      classNamePrefix="select"
    />
  );
}

export default ModuleFieldsMultiSelect;
